/**
 * RefundSlice Core Reducers
 * 
 * Main refund entity operations.
 */

import { CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import { RefundSliceState } from '../state';
import { RefundView } from '../../types/refundTypes';
import { createDefaultRefund } from '../../createDefaultRefund';

// SessionStorage backup helper
const saveBackup = (state: RefundSliceState) => {
  if (state.currentRefund) {
    try {
      sessionStorage.setItem('refund_backup', JSON.stringify({
        refund: state.currentRefund,
        isEdited: state.isEdited,
        editedFields: state.editedFields
      }));
    } catch (e) {
      // Ignore sessionStorage errors
    }
  }
};

/**
 * Update refund with partial data
 */
export const updateRefund: CaseReducer<RefundSliceState, PayloadAction<Partial<RefundView>>> = (
  state,
  action
) => {
  if (state.currentRefund) {
    state.currentRefund = { ...state.currentRefund, ...action.payload };
    state.isEdited = true;
    // Add all updated fields to editedFields
    Object.keys(action.payload).forEach(field => {
      if (!state.editedFields.includes(field)) {
        state.editedFields.push(field);
      }
    });
  } else {
    // If no current refund, set it directly from payload
    state.currentRefund = action.payload as RefundView;
    state.isEdited = false;
    state.editedFields = [];
  }
  
  saveBackup(state);
};

/**
 * Restore refund from sessionStorage backup (for HMR recovery)
 */
export const restoreFromBackup: CaseReducer<RefundSliceState> = (state) => {
  try {
    const backup = sessionStorage.getItem('refund_backup');
    if (backup) {
      const parsed = JSON.parse(backup);
      state.currentRefund = parsed.refund;
      state.isEdited = parsed.isEdited;
      state.editedFields = parsed.editedFields;
    }
  } catch (e) {
    // Ignore restore errors
  }
};

/**
 * Clear sessionStorage backup
 */
export const clearBackup: CaseReducer<RefundSliceState> = () => {
  try {
    sessionStorage.removeItem('refund_backup');
  } catch (e) {
    // Ignore sessionStorage errors
  }
};

/**
 * Create a new default refund
 */
export const createNewRefund: CaseReducer<
  RefundSliceState,
  PayloadAction<{ company?: string; center?: string; caseId?: string }>
> = (state, action) => {
  const defaultRefund = createDefaultRefund();
  if (action.payload) {
    defaultRefund.company = action.payload.company || '';
    defaultRefund.center = action.payload.center || '';
    defaultRefund.caseId = action.payload.caseId || '';
  }
  state.currentRefund = defaultRefund;
  state.isEdited = false;
  state.editedFields = [];
  state.validationErrors = {};
};

/**
 * Reset refund (only if not edited)
 */
export const resetRefund: CaseReducer<RefundSliceState> = (state) => {
  // Only reset if we don't have unsaved changes to prevent accidental data loss
  if (!state.isEdited) {
    state.currentRefund = null;
    state.isEdited = false;
    state.editedFields = [];
    state.validationErrors = {};
  }
};

/**
 * Force reset refund even with unsaved changes
 */
export const forceResetRefund: CaseReducer<RefundSliceState> = (state) => {
  state.currentRefund = null;
  state.isEdited = false;
  state.editedFields = [];
  state.validationErrors = {};
  
  // Clear backup
  try {
    sessionStorage.removeItem('refund_backup');
  } catch (e) {
    // Ignore sessionStorage errors
  }
};
